import React, { useEffect, useState } from "react";
import CharLimitHint from "./shared/CharLimitHint";
import { POLICY_TEXT_LIMITS, tooLongMessage, apiErrorMessage } from "../utils/textLimits";
import { POLICY_CATEGORIES } from "./PolicyLifecyclePanel";
import { downloadBlob } from "../utils/downloadBlob";
import { policyFileError } from "../utils/policyFileLimits";

// AI Policy Generator (extracted unchanged from SecurityInsightsDashboard's "Policies" tab) plus the
// "Use in lifecycle" dialog that turns a generated document into a policy in the register.
// Rendered on the top-level Policies tab and as the first Policy Docs sub-tab.
const POLICY_TYPES = [
    "Information Security Policy", "Access Control Policy", "Data Classification Policy",
    "Incident Response Policy", "Business Continuity Policy", "Change Management Policy",
    "Vendor Risk Management Policy", "Acceptable Use Policy", "Encryption Policy",
    "Audit & Logging Policy", "Custom…"
];
// Names are sent to the generator verbatim; the AI names reuse the spelling the rest of the app
// uses (AI assessment framework badges). PolicyGeneratorService.FrameworkGlossary describes the
// ones a model tends to confuse with a better-known neighbour (GovRAMP vs FedRAMP).
const FRAMEWORK_GROUPS = [
    {
        label: "Security & Compliance",
        frameworks: ["SOC 2", "ISO 27001", "NIST CSF", "CIS", "PCI DSS", "HIPAA", "GDPR", "NIST 800-53", "FedRAMP", "GovRAMP", "CMMC"],
    },
    {
        label: "AI Governance & Security",
        frameworks: [
            "NIST AI RMF", "NIST AI 600-1 (GenAI Profile)", "ISO 42001", "ISO/IEC 23894", "ISO/IEC 42005",
            "EU AI Act", "OWASP LLM Top 10", "MITRE ATLAS", "Google SAIF", "CSA AI Controls Matrix",
            "OECD AI Principles", "UK AI Cyber Security Code of Practice", "Singapore Model AI Governance Framework",
            "Colorado AI Act", "HITRUST AI Security",
        ],
    },
];
const INDUSTRIES = ["", "Financial Services", "Healthcare", "Technology", "Retail", "Manufacturing", "Government", "Education", "Legal", "Other"];
const SIZES      = ["", "1–50 employees", "51–100 employees", "101–500 employees", "501–1,000 employees", "1,000+ employees"];
const CLOUDS     = ["AWS", "Azure", "GCP", "On-Premise"];
const DATA_TYPES = ["PII", "Financial", "Health (PHI)", "IP / Trade Secrets", "Government / Classified"];
const REVIEW_CYCLES = [{ v: 12, label: "Annual" }, { v: 6, label: "Semiannual" }, { v: 3, label: "Quarterly" }];
// Mirrors PolicyGenerationLimits on the backend; the API rejects anything past these.
const MAX_SOURCES = 10;
const MAX_INSTRUCTION_CHARS = 5000;
// Review step shows the author's own words; instructions can run to MAX_INSTRUCTION_CHARS, so
// long ones are cut here with the full length noted.
const INSTRUCTION_PREVIEW_CHARS = 300;
const instructionsPreview = (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed) return "—";
    if (trimmed.length <= INSTRUCTION_PREVIEW_CHARS) return trimmed;
    return `${trimmed.slice(0, INSTRUCTION_PREVIEW_CHARS)}… (${trimmed.length.toLocaleString()} characters)`;
};
const MAX_POLICY_NAME_CHARS = 150;   // PolicyGeneratedDocuments.PolicyType NVARCHAR(150)
// Drafts are written in the background (AX-287); the list refreshes this often while one is generating.
const GENERATION_POLL_MS = 10_000;
// Rows written before background generation have no status; they were only ever saved once complete.
const isReady = (policy) => !policy.status || policy.status === "Ready";

const PolicyGeneratorTab = ({ apiCall, controls = [], generatedPolicies = [], setGeneratedPolicies, policyDocs = [], canManage, onPolicyCreated, compact = false }) => {
    const [genModalOpen, setGenModalOpen] = useState(false);
    const [genStep, setGenStep] = useState(1);
    const [genPolicyType, setGenPolicyType] = useState("");
    const [genCustomType, setGenCustomType] = useState("");
    const [genFrameworks, setGenFrameworks] = useState([]);
    const [genMode, setGenMode] = useState("BrandNew");
    const [genIndustry, setGenIndustry] = useState("");
    const [genSize, setGenSize] = useState("");
    const [genCloud, setGenCloud] = useState([]);
    const [genData, setGenData] = useState([]);
    const [genLoading, setGenLoading] = useState(false);
    const [genError, setGenError] = useState(null);
    // Reference material (step 4): supporting documents + the author's own comments. Documents are
    // uploaded and staged as they are picked, then attached to the policy the generation produces.
    const [genInstructions, setGenInstructions] = useState("");
    const [genSources, setGenSources] = useState([]);
    const [genUploading, setGenUploading] = useState(false);
    const [genUploadError, setGenUploadError] = useState(null);

    // "Use in lifecycle" dialog
    const [useFor, setUseFor] = useState(null);          // generated policy being pushed into the lifecycle
    const [target, setTarget] = useState(null);           // GET from-generated/{id}/target
    const [useForm, setUseForm] = useState({});
    const [useBusy, setUseBusy] = useState(false);
    const [useError, setUseError] = useState(null);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        if (!canManage) return;
        apiCall("GET", "/security-insights/policy-docs/members").then(u => setUsers(u || [])).catch(() => {});
    }, [apiCall, canManage]);

    // While any draft is still being written, refresh the list so it turns ready (or failed) on its
    // own. Background polling: an auth blip must not bounce the page through sign-in.
    const hasGenerating = generatedPolicies.some(p => p.status === "Generating");
    useEffect(() => {
        if (!hasGenerating) return;
        const timer = setInterval(() => {
            apiCall("GET", "/policy-generator", null, false, null, true)
                .then(rows => { if (Array.isArray(rows)) setGeneratedPolicies(rows); })
                .catch(() => {});
        }, GENERATION_POLL_MS);
        return () => clearInterval(timer);
    }, [hasGenerating, apiCall, setGeneratedPolicies]);

    const resetGenModal = () => {
        setGenModalOpen(false); setGenStep(1); setGenPolicyType(""); setGenCustomType("");
        setGenFrameworks([]); setGenMode("BrandNew"); setGenIndustry(""); setGenSize("");
        setGenCloud([]); setGenData([]); setGenError(null);
        setGenInstructions(""); setGenSources([]); setGenUploadError(null);
    };

    const openGenModal = () => {
        resetGenModal();
        setGenModalOpen(true);
        // Files this user staged but never generated from — from a session that was closed rather
        // than cancelled. Showing them is the only way they can be reused or cleared.
        apiCall("GET", "/policy-generator/sources")
            .then(rows => { if (rows?.length > 0) setGenSources(rows); })
            .catch(() => {});
    };

    // Closing without generating discards the staged uploads, so abandoned files don't pile up in
    // storage. A failed delete is not worth blocking the close over.
    const cancelGenModal = () => {
        genSources.forEach(s => apiCall("DELETE", `/policy-generator/sources/${s.id}`).catch(() => {}));
        resetGenModal();
    };

    const uploadSources = async (fileList) => {
        const picked = Array.from(fileList || []);
        if (picked.length === 0) return;

        // Cap the whole selection, not just the button: picking 20 files at once would otherwise
        // upload all 20 and only fail later at generate.
        const room = MAX_SOURCES - genSources.length;
        const files = picked.slice(0, Math.max(room, 0));
        setGenUploading(true);
        setGenUploadError(picked.length > files.length
            ? `Only ${MAX_SOURCES} documents can be used in one policy — ${picked.length - files.length} not added.`
            : null);
        for (const file of files) {
            const sizeError = policyFileError(file);
            if (sizeError) {
                setGenUploadError(`${file.name}: ${sizeError}`);
                continue;
            }
            try {
                const form = new FormData();
                form.append("file", file);
                const source = await apiCall("POST", "/policy-generator/sources", form, true);
                setGenSources(prev => [...prev, source]);
            } catch (err) {
                const d = err?.response?.data;
                setGenUploadError(`${file.name}: ${d?.message || err?.message || "Upload failed."}`);
            }
        }
        setGenUploading(false);
    };

    const removeSource = async (id) => {
        setGenSources(prev => prev.filter(s => s.id !== id));
        try { await apiCall("DELETE", `/policy-generator/sources/${id}`); } catch { /* row already gone */ }
    };

    const toggleArr = (arr, setArr, val) =>
        setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);

    const generatePolicy = async () => {
        setGenLoading(true); setGenError(null);
        try {
            const effectiveType = genPolicyType === "__custom__" ? genCustomType.trim() : genPolicyType;
            const payload = {
                policyType: effectiveType,
                mode: genMode,
                frameworks: genFrameworks,
                orgContext: { industry: genIndustry, companySize: genSize, cloudProviders: genCloud, dataTypes: genData },
                controls: genMode === "ControlBased"
                    ? controls.map(c => ({ controlId: c.controlId, title: c.title, category: c.category, status: c.status, score: `${Math.round(Number(c.currentScore) || 0)}/${Math.round(Number(c.maxScore) || 0)}` }))
                    : null,
                instructions: genInstructions.trim() || null,
                sourceIds: genSources.map(s => s.id),
            };
            // Accepted at once: the draft is listed as generating and fills in when the worker is done.
            const doc = await apiCall("POST", "/policy-generator/generate", payload);
            setGeneratedPolicies(prev => [doc, ...prev]);
            resetGenModal();
        } catch (err) {
            const d = err?.response?.data;
            // The API refuses rather than quietly generating from fewer documents. Drop the ones it
            // named and send the user back to the step where they can fix it.
            if (d?.unavailableSourceIds?.length > 0) {
                const gone = new Set(d.unavailableSourceIds);
                setGenSources(prev => prev.filter(s => !gone.has(s.id)));
                setGenStep(4);
            }
            setGenError(d?.message || d?.title || (typeof d === "string" ? d.slice(0, 300) : null) || err?.message || "Generation failed.");
        }
        finally { setGenLoading(false); }
    };

    // Deleting a draft that is still generating cancels it: the result is discarded and its
    // reference documents go back to the author's staged list.
    const deleteGeneratedPolicy = async (id, generating = false) => {
        if (!window.confirm(generating ? "Cancel generating this policy?" : "Delete this generated policy?")) return;
        try {
            await apiCall("DELETE", `/policy-generator/${id}`);
            setGeneratedPolicies(prev => prev.filter(p => p.id !== id));
        } catch (err) { alert(err?.response?.data?.message || "Failed to delete policy."); }
    };

    const downloadPolicy = (policy) => {
        downloadBlob(new Blob([policy.contentText], { type: "text/plain" }),
            `${policy.title.replace(/[^a-z0-9]/gi, "_")}_v${policy.version}.txt`);
    };

    // Source files are streamed by the API (never a public blob URL), so download through apiCall.
    const downloadSource = async (source) => {
        try {
            const blob = await apiCall("GET", `/policy-generator/sources/${source.id}/download`, null, false, "blob");
            downloadBlob(blob, source.fileName);
        } catch { alert("Could not download this document."); }
    };

    // ── Use in lifecycle ─────────────────────────────────────────────────────
    const inLifecycle = (policy) => policyDocs.find(d => d.sourceGeneratedDocumentId === policy.id);

    const openUse = async (policy) => {
        setUseFor(policy); setUseError(null); setTarget(null);
        setUseForm({ target: "custom", slot: null, title: policy.title, category: "", ownerUserId: "", reviewCycleMonths: 12,
                     effectiveDate: "", nextReviewDate: "", expirationDate: "", reviewerUserIds: [] });
        try {
            const t = await apiCall("GET", `/security-insights/policy-docs/from-generated/${policy.id}/target`);
            setTarget(t);
            if (t?.suggestedSlot && !t.slotOccupiedBy) setUseForm(f => ({ ...f, target: "slot", slot: t.suggestedSlot }));
        } catch { /* custom stays the default */ }
    };

    const submitUse = async () => {
        const tooLong = tooLongMessage("Title", useForm.title, POLICY_TEXT_LIMITS.policyTitle);
        if (tooLong) { setUseError(tooLong); return; }
        setUseBusy(true); setUseError(null);
        try {
            const body = {
                target: useForm.target, slot: useForm.target === "slot" ? useForm.slot : null,
                title: useForm.title, category: useForm.category || null,
                ownerUserId: useForm.ownerUserId ? parseInt(useForm.ownerUserId) : null,
                reviewCycleMonths: useForm.reviewCycleMonths || null,
                effectiveDate: useForm.effectiveDate || null, nextReviewDate: useForm.nextReviewDate || null, expirationDate: useForm.expirationDate || null,
                reviewerUserIds: useForm.reviewerUserIds,
            };
            await apiCall("POST", `/security-insights/policy-docs/from-generated/${useFor.id}`, body);
            setUseFor(null);
            onPolicyCreated?.();
        } catch (e) {
            setUseError(apiErrorMessage(e, "Could not add the policy to the lifecycle."));
        } finally { setUseBusy(false); }
    };

    const effectiveType  = genPolicyType === "__custom__" ? genCustomType.trim() : genPolicyType;
    const controlCount   = controls.length;
    const categoryCount  = new Set(controls.map(c => c.category)).size;
    const canNext1 = genPolicyType && (genPolicyType !== "__custom__" || genCustomType.trim());
    const canNext3 = genIndustry && genSize;
    const canGenerate = canNext1 && genMode;
    const inputCls = "w-full text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500";

    return (
        <div className={compact ? "" : "max-w-5xl mx-auto px-5 pb-8 pt-4"}>

            {/* Header */}
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h2 className="text-base font-bold text-gray-800">AI Policy Generator</h2>
                    <p className="text-xs text-gray-500 mt-0.5">Generate audit-ready policy documents from your existing controls or from GRC standards, then add them to the policy lifecycle for review, approval and publication.</p>
                </div>
                {canManage && (
                    <button onClick={openGenModal}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-colors shadow-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
                        Generate New Policy
                    </button>
                )}
            </div>

            {/* Generated Policies list */}
            {generatedPolicies.length === 0 ? (
                <div className="border border-dashed border-gray-300 rounded-2xl p-12 text-center">
                    <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                    <div className="text-sm font-medium text-gray-500">No policies generated yet</div>
                    <div className="text-xs text-gray-400 mt-1">Click "Generate New Policy" to create your first AI-drafted policy document, then use it in the lifecycle.</div>
                </div>
            ) : (
                <div className="space-y-3">
                    {generatedPolicies.map(policy => {
                        const linked = inLifecycle(policy);
                        const ready = isReady(policy);
                        const generating = policy.status === "Generating";
                        return (
                        <div key={policy.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
                            <div className="flex items-start gap-3">
                                <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4.5 h-4.5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-semibold text-gray-800">{policy.title}</span>
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${policy.mode === "ControlBased" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                                            {policy.mode === "ControlBased" ? "Control-Based" : "Brand New"}
                                        </span>
                                        {generating && (
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 flex items-center gap-1">
                                                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a10 10 0 100 10z"/></svg>
                                                Generating…
                                            </span>
                                        )}
                                        {policy.status === "Failed" && (
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">Failed</span>
                                        )}
                                        {linked && (
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">
                                                In lifecycle → {linked.customTitle || linked.fileName} · v{linked.version} {linked.lifecycleStatus}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                                        {policy.frameworks?.map(f => (
                                            <span key={f} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 font-medium">{f}</span>
                                        ))}
                                        <span className="text-xs text-gray-400">{new Date(policy.generatedAt).toLocaleDateString()}</span>
                                    </div>
                                    {policy.orgContext?.industry && (
                                        <div className="text-xs text-gray-400 mt-0.5">{policy.orgContext.industry} · {policy.orgContext.companySize}</div>
                                    )}
                                    {/* What this draft was written from — kept with the policy so a reviewer
                                        can read the same source material the generator was given. */}
                                    {policy.sources?.length > 0 && (
                                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1.5 flex-wrap">
                                            <span className="text-gray-400">Based on</span>
                                            {policy.sources.map(s => (
                                                <button key={s.id} onClick={() => downloadSource(s)} title="Download this source document"
                                                    className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-indigo-50 hover:text-indigo-700 font-medium">
                                                    📎 {s.fileName}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {policy.instructions && (
                                        <div className="text-xs text-gray-500 mt-1">
                                            <span className="text-gray-400">Instructions:</span> {policy.instructions.length > 160 ? `${policy.instructions.slice(0, 160)}…` : policy.instructions}
                                        </div>
                                    )}
                                    {generating && (
                                        <div className="text-xs text-gray-500 mt-1">Being written now. You can leave this page; it will be ready here in a few minutes.</div>
                                    )}
                                    {/* Failure reason, or on a ready draft the partial-attach warning. */}
                                    {policy.statusMessage && !generating && (
                                        <div className={`text-xs mt-1 ${policy.status === "Failed" ? "text-red-600" : "text-amber-700"}`}>{policy.statusMessage}</div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {canManage && ready && !linked && (
                                        <button onClick={() => openUse(policy)}
                                            className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors">
                                            Use in lifecycle
                                        </button>
                                    )}
                                    {ready && (
                                    <button onClick={() => downloadPolicy(policy)}
                                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold transition-colors">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                                        Download
                                    </button>
                                    )}
                                    {canManage && (
                                        <button onClick={() => deleteGeneratedPolicy(policy.id, generating)}
                                            className="text-xs px-2 py-1.5 border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                                            {generating ? "Cancel" : "Delete"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            {/* ── Use in lifecycle dialog ── */}
            {useFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                        <div className="bg-green-600 px-6 py-4 flex items-center justify-between">
                            <div>
                                <h2 className="text-sm font-bold text-white">Use in lifecycle</h2>
                                <div className="text-xs text-green-100">{useFor.title} becomes a Draft policy (v1) in the register.</div>
                            </div>
                            <button onClick={() => setUseFor(null)} className="text-green-100 hover:text-white">×</button>
                        </div>
                        <div className="px-6 py-4 space-y-3 text-xs">
                            {useError && <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{useError}</div>}
                            <div>
                                <label className="font-semibold text-gray-600 block mb-1">Where should it live?</label>
                                <div className="flex gap-2">
                                    <button onClick={() => setUseForm(f => ({ ...f, target: "custom" }))}
                                        className={`flex-1 px-3 py-2 rounded-lg border text-left ${useForm.target === "custom" ? "border-green-500 bg-green-50" : "border-gray-200"}`}>
                                        <div className="font-semibold text-gray-800">Custom policy</div>
                                        <div className="text-[10px] text-gray-500">Alongside the 10 standard documents</div>
                                    </button>
                                    <button disabled={!target?.suggestedSlot || !!target?.slotOccupiedBy}
                                        onClick={() => setUseForm(f => ({ ...f, target: "slot", slot: target.suggestedSlot }))}
                                        className={`flex-1 px-3 py-2 rounded-lg border text-left disabled:opacity-50 ${useForm.target === "slot" ? "border-green-500 bg-green-50" : "border-gray-200"}`}>
                                        <div className="font-semibold text-gray-800">Standard slot {target?.suggestedSlot ? `#${target.suggestedSlot}` : ""}</div>
                                        <div className="text-[10px] text-gray-500">
                                            {!target?.suggestedSlot ? "No matching standard document" : target.slotOccupiedBy ? `Occupied by "${target.slotOccupiedBy}"` : target.slotName}
                                        </div>
                                    </button>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="col-span-2">Title
                                    <input value={useForm.title || ""} onChange={e => setUseForm(f => ({ ...f, title: e.target.value }))} className={inputCls} />
                                    <CharLimitHint value={useForm.title} max={POLICY_TEXT_LIMITS.policyTitle} />
                                </label>
                                <label>Category
                                    <select value={useForm.category || ""} onChange={e => setUseForm(f => ({ ...f, category: e.target.value }))} className={inputCls}>
                                        <option value="">—</option>
                                        {POLICY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </label>
                                <label>Accountable owner
                                    <select value={useForm.ownerUserId || ""} onChange={e => setUseForm(f => ({ ...f, ownerUserId: e.target.value }))} className={inputCls}>
                                        <option value="">—</option>
                                        {users.map(u => <option key={u.id} value={u.id}>{u.fullName}</option>)}
                                    </select>
                                </label>
                                <label>Review cadence
                                    <select value={useForm.reviewCycleMonths} onChange={e => setUseForm(f => ({ ...f, reviewCycleMonths: parseInt(e.target.value) }))} className={inputCls}>
                                        {REVIEW_CYCLES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
                                    </select>
                                </label>
                                <label>Effective date
                                    <input type="date" value={useForm.effectiveDate} onChange={e => setUseForm(f => ({ ...f, effectiveDate: e.target.value }))} className={inputCls} />
                                </label>
                                <label>Next review date
                                    <input type="date" value={useForm.nextReviewDate} onChange={e => setUseForm(f => ({ ...f, nextReviewDate: e.target.value }))} className={inputCls} />
                                </label>
                                <label>Expiration date
                                    <input type="date" value={useForm.expirationDate} onChange={e => setUseForm(f => ({ ...f, expirationDate: e.target.value }))} className={inputCls} />
                                </label>
                                <div className="col-span-2">
                                    <div className="font-semibold text-gray-600 mb-1">Reviewers (every reviewer must approve)</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {users.filter(u => String(u.id) !== String(useForm.ownerUserId)).map(u => {
                                            const on = useForm.reviewerUserIds.includes(u.id);
                                            return (
                                                <button key={u.id} onClick={() => setUseForm(f => ({ ...f, reviewerUserIds: on ? f.reviewerUserIds.filter(x => x !== u.id) : [...f.reviewerUserIds, u.id] }))}
                                                    className={`px-2 py-1 rounded-lg border ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-200"}`}>
                                                    {on ? "✓ " : ""}{u.fullName}
                                                </button>
                                            );
                                        })}
                                        {users.length === 0 && <span className="text-gray-400">No assignable users.</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="px-6 pb-5 flex gap-2">
                            <button onClick={() => setUseFor(null)} className="flex-1 text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium">Cancel</button>
                            <button onClick={submitUse} disabled={useBusy || !useForm.title?.trim()}
                                className="flex-1 text-sm px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50">
                                {useBusy ? "Adding…" : "Add to lifecycle as Draft"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── 4-Step Generate Modal ── */}
            {genModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">

                        {/* Modal header + step indicator */}
                        <div className="bg-indigo-600 px-6 py-4">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="text-sm font-bold text-white">Generate Policy Document</h2>
                                <button onClick={cancelGenModal} disabled={genLoading} title={genLoading ? "Generation in progress" : "Close"} className="text-indigo-200 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                                </button>
                            </div>
                            <div className="flex items-center gap-1.5">
                                {[1,2,3,4,5].map(s => (
                                    <div key={s} className={`flex-1 h-1 rounded-full transition-all ${s <= genStep ? "bg-white" : "bg-indigo-400"}`}/>
                                ))}
                            </div>
                            <div className="text-xs text-indigo-200 mt-1.5">
                                Step {genStep} of 5 — {["Policy Type","Frameworks","Mode & Context","Reference Material","Review & Generate"][genStep-1]}
                            </div>
                        </div>

                        <div className="px-6 py-5">
                            {genError && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">{genError}</div>}

                            {/* Step 1 — Policy Type */}
                            {genStep === 1 && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 block mb-1.5">What policy do you want to generate?</label>
                                        <select value={genPolicyType} onChange={e => setGenPolicyType(e.target.value)}
                                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                            <option value="">Select policy type…</option>
                                            {POLICY_TYPES.map(t => (
                                                <option key={t} value={t === "Custom…" ? "__custom__" : t}>{t}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {genPolicyType === "__custom__" && (
                                        <div>
                                            <label className="text-xs font-semibold text-gray-600 block mb-1.5">Policy name</label>
                                            <input type="text" value={genCustomType} onChange={e => setGenCustomType(e.target.value)}
                                                maxLength={MAX_POLICY_NAME_CHARS}
                                                placeholder="e.g. Remote Work Security Policy"
                                                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Step 2 — Frameworks */}
                            {genStep === 2 && (
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 block mb-3">Which compliance frameworks should this policy cover?</label>
                                    <div className="max-h-[55vh] overflow-y-auto pr-1">
                                    {FRAMEWORK_GROUPS.map(group => (
                                        <div key={group.label} className="mb-4 last:mb-0">
                                            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">{group.label}</div>
                                            <div className="grid grid-cols-2 gap-2">
                                                {group.frameworks.map(fw => (
                                                    <button key={fw} onClick={() => toggleArr(genFrameworks, setGenFrameworks, fw)}
                                                        className={`text-xs px-3 py-2 rounded-lg border font-medium text-left transition-all ${genFrameworks.includes(fw) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300"}`}>
                                                        {genFrameworks.includes(fw) ? "✓ " : ""}{fw}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    </div>
                                    <div className="text-xs text-gray-400 mt-3">Select one or more. If none selected, defaults to SOC 2 + ISO 27001 + NIST CSF.</div>
                                </div>
                            )}

                            {/* Step 3 — Mode & Org Context */}
                            {genStep === 3 && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 block mb-2">Generation mode</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {[
                                                { key: "ControlBased", label: "Control-Based", desc: `Uses your ${controlCount} existing controls across ${categoryCount} categories`, icon: "📊" },
                                                { key: "BrandNew",     label: "Brand New",     desc: "Standard policy from GRC frameworks", icon: "📄" },
                                            ].map(m => (
                                                <button key={m.key} onClick={() => setGenMode(m.key)}
                                                    className={`p-3 border rounded-xl text-left transition-all ${genMode === m.key ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-indigo-200"}`}>
                                                    <div className="text-lg mb-1">{m.icon}</div>
                                                    <div className="text-xs font-semibold text-gray-800">{m.label}</div>
                                                    <div className="text-[10px] text-gray-500 mt-0.5 leading-snug">{m.desc}</div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-xs font-semibold text-gray-600 block mb-1">Industry <span className="text-red-500">*</span></label>
                                            <select value={genIndustry} onChange={e => setGenIndustry(e.target.value)}
                                                className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                                {INDUSTRIES.map(i => <option key={i} value={i}>{i || "Select…"}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-gray-600 block mb-1">Company size <span className="text-red-500">*</span></label>
                                            <select value={genSize} onChange={e => setGenSize(e.target.value)}
                                                className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                                {SIZES.map(s => <option key={s} value={s}>{s || "Select…"}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 block mb-1.5">Cloud providers</label>
                                        <div className="flex gap-2 flex-wrap">
                                            {CLOUDS.map(c => (
                                                <button key={c} onClick={() => toggleArr(genCloud, setGenCloud, c)}
                                                    className={`text-xs px-2.5 py-1 rounded-lg border font-medium transition-all ${genCloud.includes(c) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300"}`}>
                                                    {c}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 block mb-1.5">Data types handled</label>
                                        <div className="flex gap-2 flex-wrap">
                                            {DATA_TYPES.map(d => (
                                                <button key={d} onClick={() => toggleArr(genData, setGenData, d)}
                                                    className={`text-xs px-2.5 py-1 rounded-lg border font-medium transition-all ${genData.includes(d) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300"}`}>
                                                    {d}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 4 — Reference Material (optional) */}
                            {genStep === 4 && (
                                <div className="space-y-4">
                                    <div className="text-xs text-gray-500">
                                        Optional. Anything you add here is written into the policy — upload the
                                        documents it should be based on and say what you want it to cover.
                                    </div>

                                    <div>
                                        <label htmlFor="policy-source-files" className="text-xs font-semibold text-gray-600 block mb-1.5">Supporting documents</label>
                                        <input id="policy-source-files" type="file" multiple accept=".pdf,.docx,.txt,.csv" disabled={genUploading || genSources.length >= MAX_SOURCES}
                                            onChange={e => { uploadSources(e.target.files); e.target.value = ""; }}
                                            className="block w-full text-xs text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-50" />
                                        <div className="text-[11px] text-gray-400 mt-1">PDF, DOCX, TXT or CSV · up to 20 MB each · {MAX_SOURCES} documents max</div>
                                        {genUploading && <div className="text-xs text-indigo-600 mt-1.5">Uploading and scanning…</div>}
                                        {genUploadError && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-2">{genUploadError}</div>}

                                        {genSources.length > 0 && (
                                            <div className="mt-2 space-y-1.5">
                                                {genSources.map(s => (
                                                    <div key={s.id} className="flex items-center gap-2 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
                                                        <span className="flex-1 text-gray-700 truncate">{s.fileName}</span>
                                                        {/* A readable-text count of 0 means the file parsed to nothing, so it would
                                                            contribute nothing to the policy — say so rather than silently ignoring it. */}
                                                        <span className={s.textLength > 0 ? "text-gray-400" : "text-amber-600"}>
                                                            {s.textLength > 0 ? `${s.textLength.toLocaleString()} chars` : "no readable text"}
                                                        </span>
                                                        <button onClick={() => removeSource(s.id)} className="text-gray-400 hover:text-red-600 font-bold px-1" title="Remove">×</button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 block mb-1.5">Comments and instructions</label>
                                        <textarea value={genInstructions} onChange={e => setGenInstructions(e.target.value)} rows={5} maxLength={MAX_INSTRUCTION_CHARS}
                                            placeholder="e.g. Align with our existing incident response runbook. Data retention must be 7 years. Name the CISO as the accountable owner throughout."
                                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                        <div className="text-[11px] text-gray-400 mt-1">{genInstructions.length.toLocaleString()} / {MAX_INSTRUCTION_CHARS.toLocaleString()} characters</div>
                                    </div>
                                </div>
                            )}

                            {/* Step 5 — Review & Generate */}
                            {genStep === 5 && (
                                <div className="space-y-3">
                                    <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-xs">
                                        {[
                                            { label: "Policy type",   value: effectiveType },
                                            { label: "Mode",          value: genMode === "ControlBased" ? `Control-Based (${controlCount} controls)` : "Brand New" },
                                            { label: "Frameworks",    value: genFrameworks.length > 0 ? genFrameworks.join(", ") : "SOC 2, ISO 27001, NIST CSF (default)" },
                                            { label: "Industry",      value: genIndustry || "—" },
                                            { label: "Company size",  value: genSize || "—" },
                                            { label: "Cloud",         value: genCloud.length > 0 ? genCloud.join(", ") : "—" },
                                            { label: "Data types",    value: genData.length > 0 ? genData.join(", ") : "—" },
                                            { label: "Documents",     value: genSources.length > 0 ? genSources.map(s => s.fileName).join(", ") : "—" },
                                            { label: "Instructions",  value: instructionsPreview(genInstructions) },
                                        ].map(row => (
                                            <div key={row.label} className="flex gap-2">
                                                <span className="text-gray-400 w-28 flex-shrink-0">{row.label}</span>
                                                <span className="text-gray-800 font-medium min-w-0 break-words whitespace-pre-line">{row.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-xs text-indigo-700">
                                        Generation usually takes a few minutes and runs in the background. This window closes when you start it; the policy shows as generating in your list and is ready there once complete.
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal footer */}
                        <div className="px-6 pb-5 flex gap-2">
                            {genStep > 1 && !genLoading && (
                                <button onClick={() => setGenStep(s => s - 1)}
                                    className="flex-1 text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium transition-colors">
                                    ← Back
                                </button>
                            )}
                            {genStep < 5 && (
                                <button onClick={() => setGenStep(s => s + 1)}
                                    disabled={(genStep === 1 && !canNext1) || (genStep === 3 && !canNext3) || (genStep === 4 && genUploading)}
                                    className="flex-1 text-sm px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                                    Next →
                                </button>
                            )}
                            {genStep === 5 && (
                                <button onClick={generatePolicy} disabled={genLoading || !canGenerate}
                                    className="flex-1 text-sm px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
                                    {genLoading
                                        ? <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a10 10 0 100 10z"/></svg>Starting…</>
                                        : "🤖 Generate Policy"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PolicyGeneratorTab;
